/**
 * Filtry + paginacja listy relacji (home) + chip „Więcej…”.
 */

import { qs, qsa } from './dom.js';

const FILTER_KEYS = ['ml_status', 'ml_cat', 'ml_date', 'ml_team', 'ml_page'];
const MORE_DEFAULT_LABEL = 'Więcej…';

function readFilters(root = document) {
    const params = new URLSearchParams();
    const listRoot = qs('[data-match-list]', root) || qs('[data-match-list]');
    const filters = qs('[data-match-list-filters]', listRoot || root) || qs('[data-match-list-filters]');
    if (!filters) {
        return params;
    }

    qsa('.match-list-chip.is-active', filters).forEach((chip) => {
        const name = chip.getAttribute('data-filter-name');
        const value = chip.getAttribute('data-filter-value') ?? '';
        if (name && value !== '') {
            params.set(name, value);
        }
    });

    const team = qs('[data-filter-team]', filters);
    if (team instanceof HTMLInputElement) {
        const v = team.value.trim();
        if (v !== '') {
            params.set('ml_team', v);
        }
    }

    const page = listRoot?.getAttribute('data-ml-page') || '1';
    if (page && page !== '1') {
        params.set('ml_page', page);
    }

    return params;
}

export function matchListPollUrl(matchSlug = '') {
    const params = readFilters();
    if (matchSlug) {
        params.set('match', matchSlug);
    }
    const q = params.toString();
    return 'matchlist.php' + (q ? '?' + q : '');
}

function setPage(listRoot, page) {
    if (!listRoot) {
        return;
    }
    listRoot.setAttribute('data-ml-page', String(Math.max(1, page)));
}

function closeMoreMenu(moreRoot) {
    if (!moreRoot) {
        return;
    }
    const menu = qs('[data-more-menu]', moreRoot);
    const trigger = qs('[data-more-trigger]', moreRoot);
    if (menu) {
        menu.hidden = true;
    }
    if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
    }
    moreRoot.classList.remove('is-open');
}

function openMoreMenu(moreRoot) {
    if (!moreRoot) {
        return;
    }
    const menu = qs('[data-more-menu]', moreRoot);
    const trigger = qs('[data-more-trigger]', moreRoot);
    if (menu) {
        menu.hidden = false;
    }
    if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
    }
    moreRoot.classList.add('is-open');
}

function syncMoreChip(filters, categoryValue) {
    const moreRoot = qs('[data-match-list-more]', filters);
    if (!moreRoot) {
        return;
    }
    const trigger = qs('[data-more-trigger]', moreRoot);
    const labelEl = qs('[data-more-label]', moreRoot);
    if (!(trigger instanceof HTMLElement)) {
        return;
    }

    const option = categoryValue
        ? qs(`[data-more-value="${categoryValue}"]`, moreRoot)
        : null;
    const isMore = !!option;

    qsa('[data-more-value]', moreRoot).forEach((el) => {
        const selected = el.getAttribute('data-more-value') === categoryValue;
        el.classList.toggle('is-selected', selected);
        el.setAttribute('aria-selected', selected ? 'true' : 'false');
    });

    if (isMore && option) {
        const label = option.getAttribute('data-more-label') || categoryValue;
        trigger.setAttribute('data-filter-value', categoryValue);
        trigger.classList.add('is-active');
        trigger.setAttribute('aria-pressed', 'true');
        if (labelEl) {
            labelEl.textContent = label;
        }
        moreRoot.classList.add('is-open-value');
    } else {
        trigger.setAttribute('data-filter-value', '');
        trigger.classList.remove('is-active');
        trigger.setAttribute('aria-pressed', 'false');
        if (labelEl) {
            labelEl.textContent = MORE_DEFAULT_LABEL;
        }
        moreRoot.classList.remove('is-open-value');
    }
    closeMoreMenu(moreRoot);
}

function setCategory(filters, value) {
    qsa('.match-list-chip[data-filter-name="ml_cat"]', filters).forEach((chip) => {
        if (chip.hasAttribute('data-more-trigger')) {
            return;
        }
        const chipValue = chip.getAttribute('data-filter-value') ?? '';
        const active = chipValue === value;
        chip.classList.toggle('is-active', active);
        chip.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    syncMoreChip(filters, value);
}

function setChipGroup(filters, name, value) {
    if (name === 'ml_cat') {
        setCategory(filters, value);
        return;
    }
    qsa(`.match-list-chip[data-filter-name="${name}"]`, filters).forEach((chip) => {
        const chipValue = chip.getAttribute('data-filter-value') ?? '';
        const active = chipValue === value;
        chip.classList.toggle('is-active', active);
        chip.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
}

export function initMatchListFilters(options = {}) {
    const { onChange } = options;
    const root = qs('[data-match-list]') || document;
    const filters = qs('[data-match-list-filters]', root);
    if (!filters || filters.dataset.bound === '1') {
        return;
    }
    filters.dataset.bound = '1';

    let teamTimer = null;

    const emitChange = (resetPage = true) => {
        if (resetPage) {
            setPage(root instanceof HTMLElement ? root : qs('[data-match-list]'), 1);
        }
        if (typeof onChange === 'function') {
            onChange();
        }
    };

    filters.addEventListener('click', (event) => {
        const moreOption = event.target.closest('[data-more-value]');
        if (moreOption instanceof HTMLElement && filters.contains(moreOption)) {
            event.preventDefault();
            const value = moreOption.getAttribute('data-more-value') || '';
            setCategory(filters, value);
            emitChange(true);
            return;
        }

        const moreTrigger = event.target.closest('[data-more-trigger]');
        if (moreTrigger instanceof HTMLElement && filters.contains(moreTrigger)) {
            event.preventDefault();
            const moreRoot = moreTrigger.closest('[data-match-list-more]');
            const current = moreTrigger.getAttribute('data-filter-value') || '';
            if (current !== '') {
                // Ponowne kliknięcie wybranej kategorii z „Więcej” → reset.
                setCategory(filters, '');
                emitChange(true);
                return;
            }
            if (moreRoot?.classList.contains('is-open')) {
                closeMoreMenu(moreRoot);
            } else {
                openMoreMenu(moreRoot);
            }
            return;
        }

        const chip = event.target.closest('.match-list-chip');
        if (!(chip instanceof HTMLElement) || !filters.contains(chip)) {
            return;
        }
        event.preventDefault();
        const name = chip.getAttribute('data-filter-name');
        let value = chip.getAttribute('data-filter-value') ?? '';
        if (!name) {
            return;
        }
        if (chip.classList.contains('is-active') && value !== '' && name !== 'ml_status') {
            value = '';
        }
        setChipGroup(filters, name, value);
        emitChange(true);
    });

    document.addEventListener('click', (event) => {
        const moreRoot = qs('[data-match-list-more]', filters);
        if (!moreRoot || !moreRoot.classList.contains('is-open')) {
            return;
        }
        if (event.target instanceof Node && moreRoot.contains(event.target)) {
            return;
        }
        closeMoreMenu(moreRoot);
    });

    const team = qs('[data-filter-team]', filters);
    if (team instanceof HTMLInputElement) {
        team.addEventListener('input', () => {
            window.clearTimeout(teamTimer);
            teamTimer = window.setTimeout(() => emitChange(true), 350);
        });
        team.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                window.clearTimeout(teamTimer);
                emitChange(true);
            }
        });
    }

    const pagination = qs('[data-match-list-pagination]', root);
    if (pagination && pagination.dataset.bound !== '1') {
        pagination.dataset.bound = '1';
        pagination.addEventListener('click', (event) => {
            const btn = event.target.closest('[data-ml-page]');
            if (!(btn instanceof HTMLElement) || btn.hasAttribute('disabled')) {
                return;
            }
            event.preventDefault();
            const page = parseInt(btn.getAttribute('data-ml-page') || '1', 10);
            if (!Number.isFinite(page) || page < 1) {
                return;
            }
            setPage(root instanceof HTMLElement ? root : qs('[data-match-list]'), page);
            emitChange(false);
            const container = qs('.matchlist-container');
            if (container) {
                container.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });
    }

    const url = new URL(window.location.href);
    FILTER_KEYS.forEach((key) => {
        if (!url.searchParams.has(key)) {
            return;
        }
        const value = url.searchParams.get(key) ?? '';
        if (key === 'ml_team') {
            if (team instanceof HTMLInputElement) {
                team.value = value;
            }
            return;
        }
        if (key === 'ml_page') {
            setPage(root instanceof HTMLElement ? root : qs('[data-match-list]'), parseInt(value, 10) || 1);
            return;
        }
        setChipGroup(filters, key, value);
    });
}

export function shrinkLongMatchListTitles(root = document) {
    qsa('.ml-card__title', root).forEach((span) => {
        if ((span.textContent ?? '').length > 42) {
            span.style.fontSize = '1.15rem';
        }
    });
}
